// src/components/Markers/MapMarkers.jsx
import React, {
  useState,
  useEffect,
  useMemo,
  useRef,
  useCallback
} from 'react';
import { Source, Layer, useMap, Popup } from 'react-map-gl';
import './MapMarkers.css';
import Select from 'react-select';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import API_BASE_URL from '../../config';
import { useOutletContext } from 'react-router-dom';

export default function MapMarkers({ onRequestClose, onCollectorOpen }) {
  const map = useMap().current;
  const navigate = useNavigate();
  const { closeCollector } = useOutletContext() || {};

  // ─── Новые ref и state для drag-панели ──────────────────────────────────────────────
  const panelRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStartY, setDragStartY] = useState(0);
  const [initialTranslate, setInitialTranslate] = useState(0);
  const [panelTranslateY, setPanelTranslateY] = useState(0);
  // ─────────────────────────────────────────────────────────────────────────────────

  // стейты для данных
  const [collectors, setCollectors] = useState([]);
  const [nodes, setNodes] = useState([]);
  const [hoveredNode, setHoveredNode] = useState(null);
  const hoveredCollectorId = useRef(null);
  const popupHoverRef = useRef(false);
  const hideTimeout = useRef(null);
  const [designs, setDesigns] = useState([]);
  const [selectedCollector, setSelectedCollector] = useState(null);
  const [isAddingFault, setIsAddingFault] = useState(false);
  const [reason, setReason] = useState('');
  const [comment, setComment] = useState('');
  const [animatePanel, setAnimatePanel] = useState(false);
  const selectedCollectorId = useRef(null);
  const [reasonsList, setReasonsList] = useState([]);
  const [materials, setMaterials] = useState('');
  const [masterName, setMasterName] = useState('');
  const [problemsList, setProblemsList] = useState([]);

  // утилиты форматирования
  const fmt = v => (v === 0 ? '???' : `${v} м`);
  const formatField = v =>
    v === null ||
    v === undefined ||
    v === '' ||
    v === 0 ||
    v === 'Информация неизвестна'
      ? '???'
      : v;

  // ─── Загрузка данных из API ───────────────────────────────────────────────────────
  useEffect(() => {
    fetch(`${API_BASE_URL}/collector/get_all_collectors`)
      .then(r => r.json())
      .then(setCollectors)
      .catch(console.error);

    fetch(`${API_BASE_URL}/node/get_all_node`)
      .then(r => r.json())
      .then(setNodes)
      .catch(console.error);

    fetch(`${API_BASE_URL}/collector/get_all_design`)
      .then(r => r.json())
      .then(setDesigns)
      .catch(console.error);
  }, []);

  // Функция для перезагрузки списка коллекторов
  const loadCollectors = () => {
    fetch(`${API_BASE_URL}/collector/get_all_collectors`)
      .then(r => r.json())
      .then(setCollectors)
      .catch(console.error);
  };

  // ─── handleSaveFault: сохраняем/обновляем поломку ─────────────────────────────────
  const handleSaveFault = async () => {
    if (!selectedCollectorId.current) return;
    try {
      const today = new Date().toISOString().slice(0, 10);

      // 1) Создаём поломку
      const addRes = await axios.post(
        `${API_BASE_URL}/breake_problem/add_breake_problem`,
        {
          collector_id: selectedCollectorId.current,
          date: today,
          reason,
          comments: comment,
          status: 'В процессе',
          materials,
          master_name: masterName,
          closing_date: null
        }
      );

      // 2) Берём ID созданной записи
      const newId =
        addRes.data?.id ||
        addRes.data?.bp_id ||
        addRes.data?.breake_problem_id;

      // 3) Если причина = «Профилактическая проверка», сразу закрываем
      if (reason === 'Профилактическая проверка' && newId) {
        await axios.put(
          `${API_BASE_URL}/breake_problem/update_breake_problem/${newId}`,
          {
            status: 'Отработано',
            closing_date: today
          }
        );
      }

      // 4) Сбросим форму и обновим
      setIsAddingFault(false);
      setReason('');
      setComment('');
      setMaterials('');
      setMasterName('');
      loadCollectors();
      loadProblems();
    } catch (err) {
      console.error('Ошибка при добавлении/обновлении поломки:', err);
    }
  };
  // ─────────────────────────────────────────────────────────────────────────────────

  // ─── Получаем список «В процессе» проблем при смене selectedCollector ─────────────
  useEffect(() => {
    if (!selectedCollector) {
      setProblemsList([]);
      return;
    }
    fetch(
      `${API_BASE_URL}/breake_problem/list_problems?status=В процессе`
    )
      .then(r => r.json())
      .then(data => {
        const filtered = data.filter(
          p => p.collector_id === selectedCollector.id
        );
        setProblemsList(filtered);
      })
      .catch(console.error);
  }, [selectedCollector]);
  // ─────────────────────────────────────────────────────────────────────────────────

  // ─── Формируем GeoJSON для коллекторов ────────────────────────────────────────────
  const lineData = useMemo(
    () => ({
      type: 'FeatureCollection',
      features: collectors.map(c => {
        const latGr = c.latitude.split('#');
        const lngGr = c.longitude.split('#');
        const segments = latGr.map((lg, i) => {
          const lats = lg.split(';').map(Number);
          const lngs = (lngGr[i] || '').split(';').map(Number);
          return lats.map((lat, j) => [lngs[j], lat]);
        });
        return {
          type: 'Feature',
          id: c.id,
          properties: {
            latStr: c.latitude,
            lngStr: c.longitude,
            is_operational: c.is_operational
          },
          geometry: {
            type: 'MultiLineString',
            coordinates: segments
          }
        };
      })
    }),
    [collectors]
  );
  // ─────────────────────────────────────────────────────────────────────────────────

  // ─── Формируем GeoJSON для узлов ──────────────────────────────────────────────────
  const nodeData = useMemo(
    () => ({
      type: 'FeatureCollection',
      features: nodes.map(n => ({
        type: 'Feature',
        id: n.id,
        properties: {
          label: n.calculated_label,
          pressure: n.free_pressure,
          z: n.z_mark_earth
        },
        geometry: {
          type: 'Point',
          coordinates: [Number(n.longitude), Number(n.latitude)]
        }
      }))
    }),
    [nodes]
  );
  // ─────────────────────────────────────────────────────────────────────────────────

  // ─── Определяем слой для коллекторов ───────────────────────────────────────────────
  const lineLayer = {
    id: 'seg-lines',
    type: 'line',
    source: 'segments',
    layout: { 'line-cap': 'round', 'line-join': 'round' },
    paint: {
      'line-color': [
        'case',
        // Если выбран или hover → общий тёмный цвет
        [
          'any',
          ['boolean', ['feature-state', 'fault'], false],
          ['boolean', ['feature-state', 'hover'], false]
        ],
        [
          'case',
          ['boolean', ['get', 'is_operational'], false],
          '#264e3a', // тёмно-зелёный
          '#8A0000' // тёмно-красный
        ],
        [
          'case',
          ['boolean', ['get', 'is_operational'], false],
          '#5cbf58', // светло-зелёный
          '#FF0000' // светло-красный
        ]
      ],
      'line-width': [
        'case',
        ['boolean', ['feature-state', 'hover'], false],
        8,
        6
      ]
    }
  };
  // ─────────────────────────────────────────────────────────────────────────────────

  // ─── Слой для узлов ────────────────────────────────────────────────────────────────
  const nodeLayer = {
    id: 'node-points',
    type: 'circle',
    source: 'nodes',
    paint: {
      'circle-radius': 6,
      'circle-color': '#eb5717',
      'circle-stroke-width': 2,
      'circle-stroke-color': '#ffffff'
    }
  };
  // ─────────────────────────────────────────────────────────────────────────────────

  // ─── Hover и Click для коллекторов + подсветка ────────────────────────────────────
  useEffect(() => {
    if (!map) return;

    // Hover по линии
    const onMove = e => {
      const f = e.features?.[0];
      if (!f || f.id == null) return;
      const id = f.id;
      if (hoveredCollectorId.current !== id) {
        if (hoveredCollectorId.current != null) {
          map.setFeatureState(
            { source: 'segments', id: hoveredCollectorId.current },
            { hover: false }
          );
        }
        hoveredCollectorId.current = id;
        map.setFeatureState(
          { source: 'segments', id },
          { hover: true }
        );
        map.getCanvas().style.cursor = 'pointer';
      }
    };

    // Убираем hover
    const onLeave = () => {
      if (hoveredCollectorId.current != null) {
        map.setFeatureState(
          { source: 'segments', id: hoveredCollectorId.current },
          { hover: false }
        );
        hoveredCollectorId.current = null;
      }
      map.getCanvas().style.cursor = '';
    };

    // Click по линии: открываем панель
    const onClick = e => {
      const feat = e.features?.[0];
      if (!feat || feat.id == null) return;
      const id = feat.id;

      // Снимаем прошлый «fault», если другой выбор
      if (
        selectedCollectorId.current !== null &&
        selectedCollectorId.current !== id
      ) {
        map.setFeatureState(
          { source: 'segments', id: selectedCollectorId.current },
          { fault: false }
        );
      }
      // Отмечаем новый выбранный «fault»
      map.setFeatureState({ source: 'segments', id }, { fault: true });
      selectedCollectorId.current = id;
      localStorage.setItem('openedCollectorId', id);

      // Собираем координаты для Polyline
      const original = lineData.features.find(f => f.id === id);
      if (!original) return;
      const flat = original.geometry.coordinates.flat();
      const coords = flat.map(([lng, lat]) => ({
        latitude: lat,
        longitude: lng
      }));
      const coll = collectors.find(c => c.id === id);
      const design =
        designs.find(d => d.id === coll.collector_design_id) || {};

      // Открываем панель
      setSelectedCollector({
        ...coll,
        design,
        images: coll.images,
        coords
      });
      setAnimatePanel(true);
      // Оповещаем родителя, что панель открылась
      if (onCollectorOpen) onCollectorOpen();
    };

    map.on('mousemove', 'seg-lines', onMove);
    map.on('mouseleave', 'seg-lines', onLeave);
    map.on('click', 'seg-lines', onClick);

    // Hover для узлов (точек)
    const onNodeEnter = e => {
      const f = e.features?.[0];
      if (!f) return;
      map.getCanvas().style.cursor = 'pointer';
      const [lng, lat] = f.geometry.coordinates;
      setHoveredNode({
        id: f.id,
        calculated_label: f.properties.label,
        z_mark_earth: f.properties.z,
        free_pressure: f.properties.pressure,
        coordinates: [lng, lat],
        screenX: e.point.x,
        screenY: e.point.y
      });
      clearTimeout(hideTimeout.current);
      popupHoverRef.current = true;
    };
    const onNodeLeave = () => {
      map.getCanvas().style.cursor = '';
      popupHoverRef.current = false;
      hideTimeout.current = setTimeout(() => {
        if (!popupHoverRef.current) setHoveredNode(null);
      }, 100);
    };

    map.on('mouseenter', 'node-hover', onNodeEnter);
    map.on('mouseleave', 'node-hover', onNodeLeave);

    // Глобальный move: если ушли слишком далеко от позиции, скрываем popup
    const onMapMove = e => {
      if (!hoveredNode) return;
      if (e.point.y > hoveredNode.screenY + 350) {
        setHoveredNode(null);
        return;
      }
      if (
        e.point.x < hoveredNode.screenX - 50 ||
        e.point.x > hoveredNode.screenX + 50
      ) {
        setHoveredNode(null);
      }
    };
    map.on('mousemove', onMapMove);

    return () => {
      map.off('mousemove', 'seg-lines', onMove);
      map.off('mouseleave', 'seg-lines', onLeave);
      map.off('click', 'seg-lines', onClick);
      map.off('mouseenter', 'node-hover', onNodeEnter);
      map.off('mouseleave', 'node-hover', onNodeLeave);
      map.off('mousemove', onMapMove);
    };
  }, [map, lineData, nodeData, hoveredNode, collectors, designs, onCollectorOpen]);

  // ─── Функция закрытия панели (вызывается крестиком, overlay, свайпом) ─────────────
  const closePanel = React.useCallback(() => {
    setAnimatePanel(false);
    setTimeout(() => {
      setSelectedCollector(null);
      setIsAddingFault(false);
      setPanelTranslateY(0);
      localStorage.removeItem('openedCollectorId');
      if (onRequestClose) onRequestClose();
      if (typeof closeCollector === 'function') {
        closeCollector();
      }
    }, 250);
  }, [onRequestClose, closeCollector]);

  // ─── При клике по карте вне коллектора закрываем панель ──────────────────────────────
  useEffect(() => {
    const onMapClick = e => {
      const hits = map.queryRenderedFeatures(e.point, {
        layers: ['seg-lines', 'node-points', 'node-hover']
      });
      if (!hits.length) {
        closePanel();
      }
    };
    map.on('click', onMapClick);
    return () => map.off('click', onMapClick);
  }, [map, closePanel]);

  // ─── Загружаем список причин поломок ────────────────────────────────────────────────
  useEffect(() => {
    fetch(`${API_BASE_URL}/breake_problem/summary/reasons`)
      .then(res => res.json())
      .then(data =>
        setReasonsList(data.map(r => ({ value: r.reason, label: r.reason })))
      )
      .catch(console.error);
  }, []);

  // ─── При смене selectedCollector сбрасываем форму и запускаем анимацию ──────────────
  useEffect(() => {
    setIsAddingFault(false);
    setReason('');
    setComment('');

    if (selectedCollector) {
      setAnimatePanel(false);
      setTimeout(() => setAnimatePanel(true), 0);
    } else {
      setAnimatePanel(false);
    }
  }, [selectedCollector]);

  // ─── Функция для загрузки списка проблем (используется выше) ─────────────────────────
  const loadProblems = useCallback(() => {
    fetch(`${API_BASE_URL}/breake_problem/list_problems?status=В процессе`)
      .then(res => res.json())
      .then(data => {
        const filtered = data.filter(
          p => p.collector_id === selectedCollector.id
        );
        setProblemsList(filtered);
      })
      .catch(console.error);
  }, [selectedCollector]);

  useEffect(() => {
    if (selectedCollector) {
      loadProblems();
    } else {
      setProblemsList([]);
    }
  }, [selectedCollector, loadProblems]);

  // ─── Восстанавливаем открытую панель из localStorage при монтировании ──────────────
  useEffect(() => {
    if (!map || collectors.length === 0) return;
    const storedId = Number(localStorage.getItem('openedCollectorId'));
    if (!storedId) return;

    const collObject = collectors.find(c => c.id === storedId);
    if (!collObject) {
      localStorage.removeItem('openedCollectorId');
      return;
    }

    map.setFeatureState({ source: 'segments', id: storedId }, { fault: true });
    selectedCollectorId.current = storedId;

    const original = lineData.features.find(f => f.id === storedId);
    const flat = original.geometry.coordinates.flat();
    const coords = flat.map(([lng, lat]) => ({
      latitude: lat,
      longitude: lng
    }));
    const design =
      designs.find(d => d.id === collObject.collector_design_id) || {};

    setSelectedCollector({
      ...collObject,
      design,
      images: collObject.images,
      coords
    });
    setAnimatePanel(true);
    if (onCollectorOpen) onCollectorOpen();
  }, [map, collectors, lineData, designs, onCollectorOpen]);


  // ─────────────────────────────────────────────────────────────────────────────────

  // ─── Touch-обработчики для drag-свайпа вниз/вверх ───────────────────────────────────
  const handleTouchStart = e => {
    const touchY = e.touches[0].clientY;
    setDragStartY(touchY);
    setInitialTranslate(panelTranslateY);
    setIsDragging(true);
  };
  const handleTouchMove = e => {
    if (!isDragging) return;
    const touchY = e.touches[0].clientY;
    const deltaY = touchY - dragStartY;
    const newTranslate = Math.max(0, initialTranslate + deltaY);
    setPanelTranslateY(newTranslate);
  };
  const handleTouchEnd = () => {
    setIsDragging(false);
    if (!panelRef.current) {
      setPanelTranslateY(0);
      return;
    }
    const panelHeight =
      panelRef.current.getBoundingClientRect().height;
    if (panelTranslateY > panelHeight / 3) {
      closePanel();
    } else {
      setPanelTranslateY(0);
    }
  };
  // ─────────────────────────────────────────────────────────────────────────────────

  return (
    <>
      {/* === Источник и слой для коллекторов === */}
      <Source id="segments" type="geojson" data={lineData}>
        <Layer {...lineLayer} />
      </Source>

      {/* === Источник и слои для узлов === */}
      <Source id="nodes" type="geojson" data={nodeData}>
        <Layer {...nodeLayer} />
        {/* Ловушка для hover */}
        <Layer
          id="node-hover"
          type="circle"
          source="nodes"
          paint={{
            'circle-radius': 12,
            'circle-color': '#000',
            'circle-opacity': 0
          }}
        />
      </Source>

      {/* === Popup для узлов === */}
      {hoveredNode && (
        <Popup
          className="node-popup"
          longitude={hoveredNode.coordinates[0]}
          latitude={hoveredNode.coordinates[1]}
          anchor="top"
          closeButton={false}
          closeOnClick={false}
          interactive={true}
        >
          <div className="popup-content">
            <div className="node-header">
              Узел №{hoveredNode.id}
            </div>
            <div className="piezometric">
              <strong>Расчетная пьезометрическая отметка:</strong>{' '}
              {fmt(hoveredNode.calculated_label)}
            </div>
            <div>
              <strong>Отметка земли:</strong>{' '}
              {fmt(hoveredNode.z_mark_earth)}
            </div>
            <div>
              <strong>Свободный напор:</strong>{' '}
              {fmt(hoveredNode.free_pressure)}
            </div>
          </div>
        </Popup>
      )}



      {/* === Нижняя панель для выбранного коллектора === */}
      {selectedCollector && (
        <div
          ref={panelRef}
          className={
            'collector-info-panel' + (animatePanel ? ' open' : '')
          }
          style={{
            transform: `translateY(${panelTranslateY}px)`,
            transition: isDragging ? 'none' : undefined
          }}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          {/* Кнопка «×» для закрытия */}
          <button
            className="panel-close"
            onClick={closePanel}
          >
            ×
          </button>

          <div className="info-body">
            {isAddingFault ? (
              <>
                <div className="TTitle">
                  {selectedCollector.name}
                </div>
                <div className="guide-text">
                  Чтобы зарегистрировать поломку укажите необходимую
                  информацию к неисправности коллектора.
                </div>
                <label className="guide-text">Причина:</label>
                <Select
                  className="modal-select"
                  classNamePrefix="modal-select"
                  options={reasonsList}
                  value={
                    reason ? { value: reason, label: reason } : null
                  }
                  onChange={opt => setReason(opt?.value || '')}
                  placeholder="Выберите причину"
                />
                <label className="guide-text">
                  Материалы:
                </label>
                <input
                  type="text"
                  className="modal-input blinn"
                  placeholder="Введите материалы"
                  value={materials}
                  onChange={e => setMaterials(e.target.value)}
                />
                <label className="guide-text">
                  Исполнитель:
                </label>
                <input
                  type="text"
                  className="modal-input blinn"
                  placeholder="Введите имя исполнителя"
                  value={masterName}
                  onChange={e => setMasterName(e.target.value)}
                />
                <label className="guide-text vvv">
                  Комментарий:
                </label>
                <textarea
                  className="modal-input blin"
                  rows="5"
                  placeholder="Комментарий…"
                  value={comment}
                  onChange={e => setComment(e.target.value)}
                  style={{ resize: 'none' }}
                />
                <button
                  className="guide-button"
                  onClick={handleSaveFault}
                >
                  Сохранить
                </button>
                <button
                  className="primary-btn"
                  onClick={() => setIsAddingFault(false)}
                >
                  Отменить
                </button>
              </>
            ) : (
              <>
                <div className="TTitle">
                  {formatField(selectedCollector.name)}
                </div>
                <div>
                  Год ввода:{' '}
                  {formatField(selectedCollector.year)}
                </div>
                <div>
                  Конструктивное решение:{' '}
                  {formatField(
                    selectedCollector.design.collector_design
                  )}
                </div>
                <div>
                  Материал:{' '}
                  {formatField(selectedCollector.material)}
                </div>
                <div>
                  Длина:{' '}
                  {formatField(selectedCollector.length)}
                </div>
                <div>
                  Расход на участке:{' '}
                  {formatField(selectedCollector.expenditure)}
                </div>
                <div className="guide-text">
                  Для просмотра подробностей о коллекторе нажмите
                  кнопку «Просмотр информации».
                </div>
                <button
                  className="guide-button"
                  onClick={() => setIsAddingFault(true)}
                >
                  Добавить поломку +
                </button>
                <button
                  className="primary-btn"
                  onClick={() =>
                    navigate(
                      `/collectors/${selectedCollector.id}`,
                      {
                        state: {
                          ...selectedCollector,
                          source: 'map'
                        }
                      }
                    )
                  }
                >
                  Просмотр информации &gt;
                </button>

               <div className="TTitleee" style={{ marginTop: '1rem' }}>
                 Текущие поломки:
               </div>
                {problemsList.length === 0 ? (
                  <li className="guide-text xzx">
                    Нет активных поломок
                  </li>
                ) : (
                  <ul className="fault-list">
                    {problemsList.map(p => (
                      <li
                        key={p.id}
                        className="guide-text"
                        style={{ cursor: 'pointer' }}
                        onClick={() =>
                          navigate(`/check/${p.id}`)
                        }
                      >
                        <strong>{p.reason}</strong> (
                        {p.date.slice(0, 10)})
                      </li>
                    ))}
                  </ul>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
}
